import { Image, Input, message, Modal, Tooltip, Upload } from 'antd';
import './index.less';
import { ReactComponent as OpenUseLogSvg } from "./icon/open-use-log.svg";
import { useContext, useEffect, useRef, useState } from 'react';
import { UserFeedback } from '@/commons/interface/feedback/user-feedback';
import { createUserFeedback, getFeedbackFileUrl, getUserFeedbacks } from '@/api/feedback/user-feedback';
import dayjs from 'dayjs';
import TextArea from 'antd/lib/input/TextArea';
import { ReactComponent as FaSendSvg } from './icon/fa_send.svg';
import { ReactComponent as FileSvg } from './icon/file.svg';
import classNames from 'classnames';
import { UserFeedbackUploadFileContext } from '@/App';
import { SOFTWARE_PROJECT_ID, SOFTWARE_PROJECT_NAME } from '@/commons/constant';
import UserFeedbackUploadPanel, { UserFeedbackFileUploadParam, UserFeedbacUploadFileLocale } from './upload-panel';
import localStore from '@/commons/store/local-store';

interface IBtnProps {
    visible: boolean
    changeVisible: (visible: boolean) => void;
}

export const UserFeedbackActiveBtn = (props: IBtnProps) => {

    return <div className='user-feedback-active-btn-container' >
        <Tooltip title="反馈">
            <div className='btn' onClick={() => props.changeVisible(true)}><OpenUseLogSvg /></div>
        </Tooltip>
    </div>
}

interface IProps {
    visible: boolean
    changeVisible: (visible: boolean) => void;
}

const Upload_BUCKET_NAME = "feedback";

interface File {
    fileMd5: string,
    fileSize: number,
    fileSuffix: string,
    filePath: string,
    url: string
}

export default function UserFeedBackPanel(props: IProps) {

    const uploadFileContext = useContext(UserFeedbackUploadFileContext);

    const projectId = SOFTWARE_PROJECT_ID;
    const projectName = SOFTWARE_PROJECT_NAME;

    const [feedbacks, setFeedbacks] = useState<UserFeedback[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [keywordValue, setKeywordValue] = useState<string>('');
    const [sendLoading, setSendLoading] = useState(false);

    const [uploadFileList, setUploadFileList] = useState([]);
    const [file, setFile] = useState<File>(null);
    const fileRef = useRef(file);

    useEffect(() => {
        if (!props.visible) return;
        getUserFeedbacks({ projectId: projectId }).then(res => {
            setFeedbacks(res.data || []);
            scrollToBottom();
        }).catch(err => {
            message.error("获取反馈失败")
        })

    }, [props.visible])

    useEffect(() => {
        fileRef.current = file;
    }, [file])

    useEffect(() => {
        window.addEventListener('keydown', onKeydown);
        return () => {
            window.removeEventListener('keydown', onKeydown);
        }
    }, [inputValue, keywordValue])

    useEffect(() => {
        if (uploadFileList.length === 0) return;
        let files: UserFeedbacUploadFileLocale[] = uploadFileList.map(item => {
            return {
                file: item,
                callbackFn: uploadCallbackFn
            }
        })
        uploadFileContext.startUpload(files)
    }, [uploadFileList])

    useEffect(() => {
        if (!uploadFileContext.visible) {
            setUploadFileList([]);
        }
    }, [uploadFileContext.visible])

    const uploadCallbackFn = (uploadFile: UserFeedbackFileUploadParam) => {
        return new Promise(resolve => {
            const file: File = {} as File;
            file.fileMd5 = uploadFile.md5;
            file.fileSize = uploadFile.fileSize;
            file.fileSuffix = uploadFile.suffix;
            file.filePath = uploadFile.filePath;

            getFeedbackFileUrl(file.filePath, Upload_BUCKET_NAME).then(res => {
                if (res.status === 200) {
                    file.url = res.data;
                    setFile({ ...file });
                }
            }).finally(() => {
                resolve(true);
            })
        })
    }

    const onKeydown = (e) => {
        if (e.ctrlKey && e.key === 'Enter') {
            if (inputValue.length <= 0 || keywordValue.length <= 0) {
                message.error('请输入反馈内容和关键词');
                return;
            };
            sendFeekback();
        } else if (e.ctrlKey && e.key === 'v') {
            navigator.clipboard.read()
                .then(items => {
                    items.forEach(item => {
                        if (item.types.includes('image/png')) {
                            item.getType('image/png').then(blob => {
                                const file = new File([blob], '反馈图片.png', { type: blob.type });
                                beforeUpload(file, [file]);
                            }).catch(err => {
                                message.error("粘贴图片失败");
                            });
                        }
                    });
                }).catch(err => {
                    message.error("获取剪贴板内容失败：" + err)
                });
        }
    }

    const sendFeekback = () => {
        const user = localStore.getUser() || { name: '未知' };
        if (!user) return;
        if (keywordValue === '') {
            message.warning('请输入主题')
            return;
        }
        if (inputValue === '') {
            message.warning('请输入内容')
            return;
        }
        let dataIn: UserFeedback = {
            userName: user.name,
            content: inputValue,
            keyword: keywordValue,
            projectId: projectId,
            projectName: projectName,
        } as UserFeedback
        // ctrl enter是事件绑定，拿不到新的文件
        if (fileRef.current != null) {
            dataIn = {
                ...dataIn,
                ...fileRef.current
            }
        }
        setSendLoading(true)
        createUserFeedback(dataIn).then(res => {
            if (res.status === 200 && res.data) {
                message.success('发送成功');
                dataIn.id = res.data;
                dataIn.gmtCreate = dayjs().valueOf();
                dataIn.gmtUpdate = dayjs().valueOf();
                setFeedbacks([...feedbacks, dataIn])
                scrollToBottom();
            }
        }).catch(() => {
            message.error('发送失败');
        }).finally(() => {
            setInputValue('');
            setKeywordValue('');
            setFile(null);
            setSendLoading(false);
        })
    }

    const scrollToBottom = () => {
        setTimeout(() => {
            const list = document.getElementById('feedback-list');
            if (list === null || list.children.length === 0) return;
            list.children[list.children.length - 1].scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 200);
    }

    const beforeUpload = (file, fileList) => {
        if (!file.type.includes('image')) {
            message.error('请上传图片');
            return false;
        }
        if (uploadFileList.length === 0) {
            setUploadFileList([...fileList]);
        }
        return false;
    }

    return <Modal className='user-feedback-modal-container' title="记录" visible={props.visible} footer={null}
        destroyOnClose width={800} onCancel={() => props.changeVisible(false)}>
        <div className='feedback-box'>
            <div id="feedback-list" className='feedback-list'>
                {feedbacks.map(feedback => {
                    return <div id={feedback.id.toString()} className='feedback-item' key={feedback.id}>
                        <div className='top'>
                            <div className='title'>
                                <div className='keyword'>{feedback?.keyword}</div>
                                <div className='username'>{feedback?.userName}</div>
                                <div className='time'>{dayjs(feedback?.gmtCreate).format('YYYY/MM/DD HH:mm')}</div>
                            </div>
                        </div>
                        <div className='content'>{feedback?.content}</div>
                        {feedback?.fileMd5 && feedback?.url && <div className='img'>
                            <Image src={feedback?.url} />
                        </div>}
                    </div>
                })}
            </div>
            <div className='feedback-input'>
                <TextArea
                    readOnly={sendLoading}
                    className='content-input'
                    placeholder='请输入反馈内容 Ctrl+Enter可发送 Ctrl+V上传粘贴板图片(仅可保留一张)'
                    autoSize={{ minRows: 4, maxRows: 4 }}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)} />
                <div className='ope-area'>
                    <Input
                        readOnly={sendLoading}
                        className='keyword-input'
                        placeholder='反馈关键词'
                        maxLength={10}
                        value={keywordValue}
                        onChange={(e) => setKeywordValue(e.target.value)} />
                    <span className='fill-remaining-space'></span>
                    <Upload showUploadList={false} beforeUpload={beforeUpload} className="file-upload-wrapper">
                        <FileSvg className='file-upload-icon' />
                    </Upload>
                    <FaSendSvg onClick={() => {
                        if (inputValue.length <= 0 || keywordValue.length <= 0) {
                            message.error('请输入反馈内容和关键词');
                            return;
                        };
                        sendFeekback();
                    }} className={classNames({
                        'send-btn': true,
                        'disabled': inputValue.length <= 0 || sendLoading
                    })} title='发送' />

                </div>
            </div>
        </div>

        <UserFeedbackUploadPanel />
    </Modal>
}